import { CalibrationLevelUIDetail } from 'libs/end-to-end/src/ui';
import { getCalibrationFramingWithSentenceDescriptorId } from 'libs/end-to-end/src/ui';
import { getCalibrationLevelUIDetails } from 'libs/end-to-end/src/ui';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { getTestCountsByBiasTypeAndDimension } from 'libs/end-to-end/src/tests-utils';
import { TestCountsForBiasType } from 'libs/end-to-end/src/tests-utils';
import React, { useMemo } from 'react';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { ExplainerVideoLink } from 'apps/sigma2-ui/src/components/focus-area/ExplainerVideoLink';
import { CalibrationTestResult } from 'apps/sigma2-ui/src/disparityAndOptimization/components/CalibrationTestResult';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const Heading: React.FC<{
  calibrationScore: React.ReactNode;
  calibrationFraming: CalibrationFraming;
  metricDefinition: MyMetricDefinition;
}> = ({ calibrationScore, calibrationFraming, metricDefinition }) => {
  const language = useLanguage();

  return (
    <Spacing direction="vertical" size="sm">
      <Spacing direction="horizontal" size="sm" className="items-center">
        <S2Typography
          variant="headline-5-alt"
          translatedContent={language.focusAreaSummaryTalentOptimizationTitle({
            calibrationFraming,
          })}
        />

        <LightTooltip
          placement="right-start"
          title={
            <S2Typography
              variant="body-6"
              translatedContent={language.focusAreaDisparityCalibrationLevelTooltip(
                {
                  calibrationFraming,
                  metricDefinition,
                },
              )}
            />
          }
        >
          <span aria-label="Information Icon">
            <IconInformationSvg svg={{ className: 'w-5 h-5' }} />
          </span>
        </LightTooltip>
      </Spacing>

      {calibrationScore}
    </Spacing>
  );
};

const CalibrationScore: React.FC<{
  calibrationLevelUiDetails: CalibrationLevelUIDetail;
  focusAreaDefinition: MyFocusAreaDefinition;
  calibrationFraming: CalibrationFraming;
}> = ({
  calibrationLevelUiDetails,
  focusAreaDefinition,
  calibrationFraming,
}) => {
  const language = useLanguage();
  const calibrationLevelInSentenceTextId = useMemo(() => {
    return getCalibrationFramingWithSentenceDescriptorId({
      calibrationFraming,
      calibrationLevel: calibrationLevelUiDetails.level,
    });
  }, [calibrationFraming, calibrationLevelUiDetails]);
  return (
    <div>
      {calibrationLevelUiDetails ? (
        <S2Typography
          variant="headline-2-alt"
          translatedContent={language[calibrationLevelInSentenceTextId]()}
          dangerouslyOverrideClassNameFn={replaceTextColor(
            calibrationLevelUiDetails.textColor,
          )}
        />
      ) : null}{' '}
      <S2Typography
        variant="body-6"
        translatedContent={language.focusAreaDisparityCalibrationCaption({
          focusAreaName: focusAreaDefinition.displayName,
          calibrationFraming,
        })}
      />
    </div>
  );
};

export const OptimizationSection = (props: {
  focusAreaDefinition: MyFocusAreaDefinition;
  metric: string;
  calibrationScore: number;
  testSummary: TestSummary;
  leftFooter?: React.ReactNode;
  tooltipMode: string;
  calibrationFraming: CalibrationFraming;
}) => {
  const {
    focusAreaDefinition,
    calibrationScore,
    testSummary,
    metric,
    leftFooter,
    tooltipMode,
    calibrationFraming,
  } = props;

  const language = useLanguage();

  const calibrationLevelUiDetails = useMemo(() => {
    const calibrationLevel = getCalibrationLevel(calibrationScore);
    return getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
  }, [calibrationScore, calibrationFraming]);

  const testResultsByBiasTypeAndDimension: Record<
    BiasType,
    TestCountsForBiasType
  > = useMemo(() => {
    const focusAreaTests = testSummary.tests.filter(
      (t) =>
        t.focusArea === focusAreaDefinition.focusArea && t.metric === metric,
    );

    return getTestCountsByBiasTypeAndDimension(focusAreaTests);
  }, [focusAreaDefinition, metric, testSummary.tests]);

  const metricDefinition = useMemo(() => {
    return focusAreaDefinition.metrics.find(
      (m) => m.columnHeaderValue === metric,
    );
  }, [focusAreaDefinition, metric]);

  return (
    <Spacing
      direction="vertical"
      size="lg"
      className="justify-between min-h-full w-full"
    >
      {metricDefinition && (
        <Heading
          calibrationScore={
            <CalibrationScore
              calibrationLevelUiDetails={calibrationLevelUiDetails}
              focusAreaDefinition={focusAreaDefinition}
              calibrationFraming={calibrationFraming}
            />
          }
          calibrationFraming={calibrationFraming}
          metricDefinition={metricDefinition}
        />
      )}

      <Spacing direction="vertical" size="xl">
        <Spacing
          direction="horizontal"
          size="sm"
          className="w-full justify-between"
        >
          <S2Typography
            variant="headline-4"
            translatedContent={language.testsResults()}
          />

          <ExplainerVideoLink />
        </Spacing>

        <Spacing direction="vertical" size="sm">
          {testResultsByBiasTypeAndDimension &&
            Object.entries(testResultsByBiasTypeAndDimension).map(
              ([biasTypeRaw, testResult]) => {
                if (getTotalTestCount(testResult) <= 0) {
                  return null;
                }

                const biasType = biasTypeRaw as BiasType;

                return (
                  <CalibrationTestResult
                    key={biasType}
                    biasType={biasType}
                    testResult={testResult}
                    tooltipMode={tooltipMode}
                    calibrationFraming={calibrationFraming}
                  />
                );
              },
            )}
        </Spacing>
      </Spacing>

      {leftFooter && (
        <Spacing direction="horizontal" size="sm" className="w-full">
          {leftFooter}
        </Spacing>
      )}
    </Spacing>
  );
};
